import React, { useEffect, useState, useContext } from "react";
import { Layout } from "@shopify/polaris";
import { useLazyQuery } from "@apollo/client";
import Moment from "moment";

import "jspdf-autotable";

// import hoc
import { withFeature, withErrorBoundary } from "lib/hoc";

// import context
import { PrivateContext } from "lib/context";

// import constant
import constant from "lib/constant/constant";

// import components
import { Banner, Spinner } from "lib/components";

// import query
import { GET_PRODUCT_EXPORT_SETTING } from "app/setup/apollo/queries";
import { GET_PRODUCT_DETAILS } from "app/productOld/apollo/queries";

// import helpers
import { baseHelper, fileHelper, storageHelper } from "lib/helpers";
import { useLocation } from "react-router-dom";
import productHelper from "./helper/product";

const ExportAsPDF = () => {
  const data = useContext(PrivateContext);
  const { cms, currentUser } = data;
  const brandLogo = storageHelper.get("logoUrl");
  const defaultLogo = constant.DEFAULT_PDF_LOGO;
  const isVendor = baseHelper.isVendor(currentUser);
  const [isPageLoading, setIsPageLoading] = useState(true);
  const [queryData, setQueryData] = useState();
  const [pdfDetails, setPDFDetails] = useState([]);
  const [hasRecord, setHasRecord] = useState(true);
  const [pdfCount, setPdfCount] = useState(0);
  const location = useLocation();
  const [
    getProductList,
    { data: getProductData, loading: productDetailsLoading, error: productError },
  ] = useLazyQuery(GET_PRODUCT_DETAILS, { variables: { input: queryData } });

  const [getProductSetting, { data: getSettingData, loading: settingLoading, error: settingError }] = useLazyQuery(
    GET_PRODUCT_EXPORT_SETTING
  );

  const [banner, setBanner] = useState({
    title: "",
    status: "",
    isOpen: false,
  });

  useEffect(() => {
    if (
      !productDetailsLoading &&
      !productError &&
      getProductData &&
      getSettingData &&
      !settingLoading &&
      !settingError
    ) {
      const resData = baseHelper.getResponseData(getProductData, constant.gql.GET_PRODUCT_DETAILS);
      const settingResponse = baseHelper.getResponseData(getSettingData, constant.gql.GET_PRODUCT_EXPORT_SETTING);

      let bannerData = false;
      if (!resData) {
        const resError = baseHelper.getResponseError(getProductData, constant.gql.GET_PRODUCT_DETAILS);
        bannerData = {
          title: resError,
          status: constant.CRITICAL,
          isOpen: true,
        };
      }

      const { productList = [], hasMore = false } = resData;
      setHasRecord(hasMore);
      if (!(productList && productList.length)) {
        bannerData = {
          title: cms("section.export.message.error.noProduct"),
          status: constant.CRITICAL,
          isOpen: true,
        };
      }
      if (bannerData) {
        setBanner(bannerData);
        setTimeout(() => {
          window.close();
        }, 2000);
        return;
      }

      const pdfData = productHelper.productCSVData(productList, settingResponse, false, true, {}, isVendor, false);

      if (pdfData && pdfData.length) {
        setPdfCount(pdfCount + 1);
        if (pdfCount > 0) {
          pdfData.shift();
        }
        setPDFDetails([...pdfDetails, ...pdfData]);
      }

      if (hasMore) {
        setQueryData({ ...queryData, page: parseInt(queryData.page, 10) + 1 });
      }
    }
  }, [
    brandLogo,
    cms,
    defaultLogo,
    getProductData,
    productError,
    productDetailsLoading,
    getSettingData,
    isVendor,
    settingError,
    settingLoading,
  ]);

  useEffect(() => {
    const callback = () => {
      setTimeout(() => {
        window.close();
      }, 500);
    };
    if (pdfDetails && pdfDetails.length > 1) {
      const date = Moment(new Date(), "DD-MM-YYYY").format("LL");
      if (!hasRecord) {
        fileHelper
          .loadImage(brandLogo || defaultLogo)
          .then((logo) => {
            fileHelper.generatePdf(logo, pdfDetails, `${constant.CONSOLIDATED_PRODUCT} ${date}`, callback);
          })
          .catch(() => {
            fileHelper.generatePdf(false, pdfDetails, `${constant.CONSOLIDATED_PRODUCT} `, callback);
          });
      }
    }
  }, [pdfDetails]);

  useEffect(() => {
    getProductList({ variables: { input: queryData } });
  }, [queryData]);

  useEffect(() => {
    const exportProduct = () => {
      const dataToFetch = { page: 1 };
      const { endDate, startDate, status, vendor } = baseHelper.queryParamsFromLocation(location);
      if (status && status.length) {
        dataToFetch.status = status.split(",");
      }
      if (vendor && vendor.length) {
        dataToFetch.vendor = vendor.split(",");
      }
      if (startDate && endDate) {
        dataToFetch.dateRange = { start: startDate, end: endDate };
      }
      setQueryData(dataToFetch);
      getProductSetting();
    };
    if (isPageLoading) {
      exportProduct();
      setIsPageLoading(false);
    }
  }, [data, getProductSetting, isPageLoading]);

  return (
    <Layout.Section>
      {productDetailsLoading && <Spinner isFullPage />}
      {banner.isOpen && (
        <Banner
          title={banner.title}
          status={banner.status}
          isOpen={banner.isOpen}
          onDismiss={() => setBanner({ isOpen: false, status: "", title: "" })}
        />
      )}
      {/* <Banner
        title="Product export is in progress"
        status="info"
        isOpen={true}
      >
        <br />
        Please wait while we are exporting your products in a batch of 500. Please allow multiple downloads if there is a popup on your screen. <br /> <br />

        This tab will automatically close once all the downloads are completed.
      </Banner> */}
    </Layout.Section>
  );
};

export default withFeature(withErrorBoundary(ExportAsPDF), { feature: constant.EXPORT_PRODUCT });
